import React, { useMemo } from "react";
import PropTypes from "prop-types";
import "./FleetHub.css";
import classNames from 'classnames';
import { GridLayout } from "components/grid";
import { Icon, Position } from "@blueprintjs/core";
import { Tooltip } from "components/tooltip";
import { PieChart } from "components/charts";

export function FleetHubWidget({
    eventTypeId,
    iconName,
    eventTypeName,
    eventSummary,
    onNavigate,
    eventStatuses,
    loading
}) {

    const handleNavigate = (groupKey) => {
        if (onNavigate) {
            onNavigate(eventTypeId, groupKey);
        }
    };

    const showTooltip = useMemo(() => {
        if (loading || !eventStatuses || !eventSummary?.eventTypeName || !eventSummary?.eventStatusTotals) {
            return false;
        }
        let totalStatusCount = eventSummary?.eventStatusTotals.reduce((acc, tot) => acc + tot.statusCount, 0);
        if (totalStatusCount === 0) {
            return false
        }
        return true;
    }, [loading, eventSummary]);

    const storagePerAreaChartData = useMemo(() => {
        if (loading || !eventStatuses || eventStatuses.length < 1 || !eventSummary?.eventTypeName || !eventSummary?.eventStatusTotals) {
            return [];
        }
        let totalStatusCount = eventSummary?.eventStatusTotals.reduce((acc, tot) => acc + tot.statusCount, 0);
        if (totalStatusCount === 0) {
            return [{
                id: -1,
                label: "",
                value: 100,
                color: "#F0F0F0"
            }];
        }

        return eventSummary.eventStatusTotals
            .sort(function (a, b) { return a.eventStatusId - b.eventStatusId })
            .map(es => {
                let eventStatus = eventStatuses.find(status => status.id === es.eventStatusId);
                return{
                    id: es.eventStatusId,
                    label: `${eventStatus?.name} - ${es.statusCount} / ${totalStatusCount}`,
                    value: ((es.statusCount / totalStatusCount) * 100).toFixed(2),
                    color: eventStatus?.statusColour
                };
            });
    }, [loading, eventSummary]);

    return (
        <div className={classNames("fleet-hub-widget", { "bp6-skeleton": loading })}>
            <button className="fleet-hub fleet-hub-widget-header" onClick={() => handleNavigate()}>
                <h6 className="inline-items">
                    {iconName && <i className={classNames("custom-icon-" + iconName, "custom-icon keyblock-icon")}></i>}
                    {eventTypeName && <>{eventTypeName}<Icon icon="chevron-right" size={20} /></>}
                </h6>
            </button>
            <div className="fleet-hub-widget-content">
                <div>
                    <PieChart
                        title=""
                        items={storagePerAreaChartData}
                        noDataMessage="No events of this type."
                        legend={{
                            show: false
                        }}
                        tooltip={{
                            show: showTooltip,
                            trigger: 'item',
                            formatter: "{b} ({d}%)"
                        }}
                        minimal={true}
                    />
                </div>

                <GridLayout numberOfColumns={3} gap={10}>
                    <button className="fleet-hub" onClick={() => handleNavigate('upcoming')}>
                        <h6>Upcoming</h6>
                        <Tooltip
                            position={Position.TOP}
                            content={<div>
                                There {eventSummary.upcomingOverdue === 1 ? 'is' : 'are'} {eventSummary.upcomingOverdue} upcoming overdue {eventTypeName}{eventSummary.upcomingOverdue === 1 ? '' : 's'}
                                </div>}
                            disabled={eventSummary.upcomingOverdue === 0}
                        >
                            <p className={classNames({ 'fleet-hub-widget-warning': eventSummary.upcomingOverdue > 0 })}>{eventSummary.upcomingTotal}</p>
                        </Tooltip>
                        <div className="fleet-hub-footer">
                            <p>View</p>
                            <Icon icon="chevron-right" size={20} />
                        </div>
                    </button>
                    <button className="fleet-hub" onClick={() => handleNavigate('inProgress')}>
                        <h6>In Progress</h6>
                        <Tooltip
                            position={Position.TOP}
                            content={<div>
                                There {eventSummary.inProgressOverdue === 1 ? 'is' : 'are'} {eventSummary.inProgressOverdue} in progress overdue {eventTypeName}{eventSummary.inProgressOverdue === 1 ? '' : 's'}
                            </div>}
                            disabled={eventSummary.inProgressOverdue === 0}
                        >
                            <p className={classNames({ 'fleet-hub-widget-warning': eventSummary.inProgressOverdue > 0 })}>{eventSummary.inProgressTotal}</p>
                        </Tooltip>
                        <div className="fleet-hub-footer">
                            <p>View</p>
                            <Icon icon="chevron-right" size={20} />
                        </div>
                    </button>
                    <button className="fleet-hub" onClick={() => handleNavigate('completed')}>
                        <h6>Completed</h6>
                        <Tooltip
                            position={Position.TOP}
                            content={<div>
                                There {eventSummary.completedFailed === 1 ? 'is' : 'are'}  {eventSummary.completedFailed} failed {eventTypeName}{eventSummary.completedFailed === 1 ? '' : 's'}
                            </div>}
                            disabled={eventSummary.completedFailed === 0}
                        >
                            <p>{eventSummary.completedTotal}</p>
                        </Tooltip>
                        <div className="fleet-hub-footer">
                            <p>View</p>
                            <Icon icon="chevron-right" size={20} />
                        </div>
                    </button>
                </GridLayout>
            </div>
        </div>
    );
}

FleetHubWidget.propTypes = {
    eventTypeId: PropTypes.number.isRequired,
    iconName: PropTypes.string.isRequired,
    eventTypeName: PropTypes.string.isRequired,
    eventSummary: PropTypes.object.isRequired,
    onNavigate: PropTypes.func,
    eventStatuses: PropTypes.array.isRequired,
    loading: PropTypes.bool
};
